import { useRightyInline, type InlineResult } from './useRightyInline'
import type { PlanAction } from '../types'
import { stripHtml } from '@/services/string'

/**
 * Generated question data returned from createQuestion
 */
export interface GeneratedQuestion {
  /** The question text */
  text: string
  /** Internal dimension name (Title Case) */
  dimensionName: string
  /** Answer options */
  options: Array<{
    /** Display label for the option */
    label: string
    /** Internal value (snake_case) */
    value: string
  }>
}

/**
 * Generated offer data returned from createOffer
 */
export interface GeneratedOffer {
  /** Internal name for the offer */
  name: string
  /** Headline/title */
  title: string
  /** Description text */
  description: string
  /** Button text */
  buttonText: string
}

/**
 * Generated code result
 */
export interface GeneratedCode {
  /** The generated JavaScript code */
  code: string
}

/**
 * Generated variants for A/B testing
 */
export interface GeneratedVariants {
  variants: Array<{
    name: string
    parts: {
      title?: string
      description?: string
      buttonText?: string
    }
  }>
}

/**
 * Generated personalization tokens
 */
export interface GeneratedPersonalization {
  /** Default/fallback text */
  fallbackText: string
  /** Mapping of segment IDs to personalized text */
  segmentMap: Record<string, string>
}

/**
 * Generated sync values for ESP custom fields
 */
export interface GeneratedSyncValues {
  /** Mapping of segment IDs to sync values */
  syncValues: Record<string, string>
}

/**
 * Generated publish message
 */
export interface GeneratedPublishMessage {
  /** Short commit-style message */
  message: string
  /** Optional longer description */
  details?: string
}

/**
 * Enriched change descriptions (new format from RightyInlineService)
 */
export interface EnrichedChanges {
  enrichedChanges: Array<{
    changeId: string
    description: string
    category: 'content' | 'targeting' | 'structure' | 'settings'
  }>
}

/**
 * Enriched changes with summary (compatible with old useClaudeApi format)
 */
export interface EnrichedChangesWithSummary {
  /** Mapping of change IDs to descriptions */
  descriptions: Record<string, string>
  /** Summary message for the publish */
  summaryMessage: string
}

/**
 * Generated internal ID for a question/dimension
 */
export interface GeneratedInternalId {
  /** The generated internal ID */
  internalId: string
}

/**
 * Generated segment group combinations
 */
export interface GeneratedSegmentCombinations {
  /** Array of children with transformed names */
  children: Array<{ name: string; [key: string]: any }>
}

/**
 * Question analysis result with audience insights
 */
export interface QuestionAnalysisResult {
  audience_insights: {
    segments: Array<{
      name: string
      description: string
      estimated_size: string
      key_characteristics: Array<{
        trait: string
        evidence: string
        confidence: number
      }>
      pain_points: Array<{
        problem: string
        voc_phrases: string[]
        frequency: number
        intensity: number
      }>
      communication_preferences: {
        language_style: string
        key_terms: string[]
        avoided_terms: string[]
      }
    }>
    market_positioning: {
      value_proposition_alignment: Array<{
        value_prop: string
        audience_reception: string
        enhancement_suggestions: string
      }>
      messaging_opportunities: Array<{
        message: string
        target_segment: string
        supporting_evidence: string
        recommended_channels: string[]
      }>
    }
    competitive_insights: {
      mentioned_alternatives: Array<{
        solution: string
        context: string
        frequency: number
      }>
      differentiation_opportunities: Array<{
        area: string
        evidence: string
        recommendation: string
      }>
    }
  }
  question_improvements: {
    revised_question: string | null
    reasoning: string
    new_options: Array<{
      text: string
      estimated_capture_rate: number
      reasoning: string
    }>
    options_to_remove: Array<{
      text: string
      reasoning: string
    }>
  }
  strategic_recommendations: {
    immediate_actions: Array<{
      action: string
      impact: string
      evidence: string
      priority: number
    }>
    long_term_opportunities: Array<{
      opportunity: string
      rationale: string
      implementation_path: string
    }>
    risk_factors: Array<{
      risk: string
      indicators: string
      mitigation_strategy: string
    }>
  }
}

/**
 * Bootstrapped flow result
 */
export interface BootstrappedFlow {
  personalizationTokens: any[]
  flow: any[]
  questions: any[]
  offers: any[]
  explanation?: string
}

/**
 * Composable for headless (programmatic) Righty AI operations.
 *
 * This provides convenience methods for common AI tasks, matching the
 * existing useClaudeApi patterns. Each method returns structured data
 * that can be used directly in the application.
 *
 * @example
 * ```typescript
 * const {
 *   createQuestion,
 *   createOffer,
 *   generateCode,
 *   isGenerating
 * } = useRightyHeadless()
 *
 * // Create a new question
 * const question = await createQuestion(
 *   'Find out what industry they work in',
 *   { existingQuestions: ['Role', 'Company Size'] }
 * )
 *
 * if (question) {
 *   console.log(question.text, question.options)
 * }
 * ```
 */
export function useRightyHeadless(options?: { teamPid?: string }) {
  const { execute, executeStreaming, isGenerating, error, reset } = useRightyInline(options)

  /**
   * Create a survey/quiz question with answer options.
   *
   * @param instruction - What kind of question to create
   * @param context - Optional context (existing questions, flow info)
   * @returns The generated question with plan action, or null on error
   */
  async function createQuestion(
    instruction: string,
    context?: {
      existingQuestions?: string[]
      flowId?: string
      flowName?: string
    }
  ): Promise<{ plan: PlanAction; question: GeneratedQuestion } | null> {
    const result = await execute({
      taskType: 'create_question',
      instruction,
      context: {
        existingQuestions: context?.existingQuestions,
        flowId: context?.flowId,
        flowName: context?.flowName
      }
    })

    if (result.success && result.plan) {
      // Extract question data from the plan
      const questionStep = result.plan.steps?.find((s) => s.type === 'question')
      if (questionStep) {
        return {
          plan: result.plan,
          question: {
            text: questionStep.data.text,
            dimensionName: questionStep.data.dimensionName,
            options: questionStep.data.options || []
          }
        }
      }
    }

    return null
  }

  /**
   * Create an offer (CTA) or form.
   *
   * @param instruction - What kind of offer/form to create
   * @param type - 'offer' for CTA, 'form' for opt-in form
   * @param context - Optional context (existing offers, questions in flow)
   * @returns The generated offer with plan action, or null on error
   */
  async function createOffer(
    instruction: string,
    type: 'offer' | 'form' = 'offer',
    context?: {
      questionsInFlow?: string[]
      existingOffers?: string[]
    }
  ): Promise<{ plan: PlanAction; offer: GeneratedOffer } | null> {
    const result = await execute({
      taskType: 'create_offer',
      instruction: `Create a ${type}: ${instruction}`,
      context: {
        questionsInFlow: context?.questionsInFlow,
        existingOffers: context?.existingOffers
      }
    })

    if (result.success && result.plan) {
      const offerStep = result.plan.steps?.find((s) => s.type === 'offer' || s.type === 'form')
      if (offerStep) {
        return {
          plan: result.plan,
          offer: {
            name: offerStep.name,
            title: offerStep.data.title,
            description: offerStep.data.description,
            buttonText: offerStep.data.buttonText
          }
        }
      }
    }

    return null
  }

  /**
   * Generate JavaScript code for a code node.
   *
   * @param instructions - What the code should do
   * @param context - Optional context (flow context, existing code)
   * @returns The generated code, or null on error
   */
  async function generateCode(
    instructions: string,
    context?: {
      flowContext?: any
      existingCode?: string
    }
  ): Promise<GeneratedCode | null> {
    const result = await execute({
      taskType: 'generate_code',
      instruction: instructions,
      context: {
        flowContext: context?.flowContext,
        existingCode: context?.existingCode
      }
    })

    if (result.success) {
      if (result.code) {
        return { code: result.code }
      }
      // Try parsing from JSON data
      if (result.data?.code) {
        return { code: result.data.code }
      }
    }

    return null
  }

  /**
   * Generate multiple copy variants for A/B testing.
   *
   * @param parts - Current offer parts to create variants of
   * @param userContext - Optional additional context
   * @returns Generated variants, or null on error
   */
  async function generateOfferVariants(
    parts: {
      title?: string
      description?: string
      buttonText?: string
    },
    userContext?: string
  ): Promise<GeneratedVariants | null> {
    const result = await execute({
      taskType: 'generate_variants',
      instruction: userContext || 'Generate copy variants for A/B testing',
      context: { parts }
    })

    if (result.success && result.data?.variants) {
      return { variants: result.data.variants }
    }

    return null
  }

  /**
   * Generate segment-specific personalization tokens.
   *
   * @param dimensionId - The dimension to generate tokens for
   * @param instruction - What kind of personalization to generate
   * @returns Generated personalization map, or null on error
   */
  async function generatePersonalization(
    dimensionId: string,
    instruction: string
  ): Promise<GeneratedPersonalization | null> {
    const result = await execute({
      taskType: 'generate_personalization',
      instruction,
      context: { dimensionId }
    })

    if (result.success && result.data) {
      return {
        fallbackText: result.data.fallbackText || '',
        segmentMap: result.data.segmentMap || {}
      }
    }

    return null
  }

  /**
   * Generate personalization tokens for each segment in a segment group.
   * This method is compatible with the old useClaudeApi.generatePersonalizationTokens API.
   *
   * @param surroundingContent - The surrounding text context where the personalization will appear
   * @param segmentGroupName - The name or question text associated with this segment group
   * @param fallbackText - The default text when no segment matches
   * @param segmentNames - Array of segment names to generate personalization for
   * @returns A mapping of segment names to personalized text
   */
  async function generatePersonalizationTokens(
    surroundingContent: string,
    segmentGroupName: string,
    fallbackText: string,
    segmentNames: string[]
  ): Promise<Record<string, string> | null> {
    // Build an instruction that provides full context
    const instruction = `Generate personalized variations of "${fallbackText}" for each segment in this dimension.

Context:
- This text appears in: ${surroundingContent || 'marketing content'}
- Segment group/question: ${segmentGroupName || 'unknown'}
- Segments to personalize for: ${segmentNames.join(', ')}

For each segment, create a variation that resonates with that specific audience while maintaining the same meaning and tone as the fallback text.`

    const result = await execute({
      taskType: 'generate_personalization',
      instruction,
      context: {
        fallbackText,
        segmentNames,
        surroundingContent,
        segmentGroupName
      }
    })

    if (result.success && result.data) {
      // The backend returns segmentMap which maps segment names to personalized text
      // This matches the old API format expected by Phrase.vue
      return result.data.segmentMap || null
    }

    return null
  }

  /**
   * Generate ESP custom field sync values for segments.
   *
   * @param dimensionId - The dimension to generate sync values for
   * @param segmentData - Array of segment IDs and names
   * @returns Generated sync values, or null on error
   */
  async function generateSyncValues(
    dimensionId: string,
    segmentData: Array<{ id: string; name: string }>
  ): Promise<GeneratedSyncValues | null> {
    const result = await execute({
      taskType: 'generate_sync_values',
      instruction: 'Generate sync values for ESP custom field',
      context: { dimensionId, segmentData }
    })

    if (result.success && result.data?.syncValues) {
      return { syncValues: result.data.syncValues }
    }

    return null
  }

  /**
   * Enrich config changes with human-readable descriptions.
   *
   * @param changes - Array of config changes to describe
   * @returns Enriched changes with descriptions, or null on error
   */
  async function enrichChanges(
    changes: Array<{
      id: string
      path: string
      oldValue?: any
      newValue?: any
    }>
  ): Promise<EnrichedChanges | null> {
    const result = await execute({
      taskType: 'enrich_changes',
      instruction: 'Generate human-readable descriptions for these changes',
      context: { changes }
    })

    if (result.success && result.data?.enrichedChanges) {
      return { enrichedChanges: result.data.enrichedChanges }
    }

    return null
  }

  /**
   * Enrich config changes with descriptions AND generate summary message.
   * This method is compatible with the old useClaudeApi.enrichChangesWithDescriptions API.
   *
   * @param changes - Array of config changes (can use the format from useConfigChanges)
   * @returns Object with descriptions (Record<string, string>) and summaryMessage
   */
  async function enrichChangesWithDescriptions(
    changes: Array<{
      id: string
      category?: string
      type?: string
      name?: string
      details?: string[]
      diff?: any
    }>
  ): Promise<EnrichedChangesWithSummary> {
    // Transform changes to the format expected by the inline service
    const formattedChanges = changes.map((change) => ({
      id: change.id,
      category: change.category,
      type: change.type,
      name: change.name,
      details: change.details,
      diff: change.diff
    }))

    // Get enriched descriptions
    const enrichResult = await execute({
      taskType: 'enrich_changes',
      instruction: 'Generate human-readable descriptions for these configuration changes',
      context: { changes: formattedChanges }
    })

    // Transform enriched changes array to Record<string, string>
    const descriptions: Record<string, string> = {}
    if (enrichResult.success && enrichResult.data?.enrichedChanges) {
      for (const change of enrichResult.data.enrichedChanges) {
        descriptions[change.changeId] = change.description
      }
    }

    // Generate summary message
    const changeSummary = changes
      .map((c) => `${c.type || 'modified'} ${c.name || c.id}`)
      .join(', ')

    const summaryResult = await execute({
      taskType: 'generate_publish_message',
      instruction: 'Generate a commit-style message for these changes',
      context: { changeSummary }
    })

    const summaryMessage =
      summaryResult.success && summaryResult.data?.message
        ? summaryResult.data.message
        : `Update ${changes.length} configuration item${changes.length === 1 ? '' : 's'}`

    return {
      descriptions,
      summaryMessage
    }
  }

  /**
   * Generate a git-style commit message for config changes.
   *
   * @param changeSummary - Summary of what changed
   * @returns Generated publish message, or null on error
   */
  async function generatePublishMessage(
    changeSummary: string
  ): Promise<GeneratedPublishMessage | null> {
    const result = await execute({
      taskType: 'generate_publish_message',
      instruction: 'Generate a commit-style message for these changes',
      context: { changeSummary }
    })

    if (result.success && result.data) {
      return {
        message: result.data.message || 'Update configuration',
        details: result.data.details
      }
    }

    return null
  }

  /**
   * Generate an internal ID (dimension name) from question text.
   * Matches naming style of existing dimensions.
   *
   * @param question - The question text to generate an ID for
   * @returns The generated internal ID, or null on error
   */
  async function createQuestionInternalId(question: string): Promise<string | null> {
    // Auto-fetch existing dimensions for style matching (like useClaudeApi did)
    const segmentGroupStore = useSegmentGroupStore()
    const existingDimensions = segmentGroupStore.segmentGroups.map((group: any) => ({
      name: group.name,
      question: group.question?.text ? stripHtml(group.question.text) : null
    }))

    const result = await execute({
      taskType: 'generate_internal_id',
      instruction: `Generate an internal ID for this question: "${question}"`,
      context: { existingDimensions }
    })

    if (result.success && result.data?.internalId) {
      return result.data.internalId
    }

    return null
  }

  /**
   * Generate short, readable names for segment group combinations.
   * Transforms "Question A *** Question B" into "Short A x Short B".
   *
   * @param payload - JSON string containing children array with names
   * @returns Transformed payload with shortened names, or null on error
   */
  async function generateSegmentGroupCombinations(
    payload: string
  ): Promise<GeneratedSegmentCombinations | null> {
    const result = await execute({
      taskType: 'generate_segment_combinations',
      instruction: 'Generate short names for these segment combinations',
      context: { payload: JSON.parse(payload) }
    })

    if (result.success && result.data) {
      return result.data as GeneratedSegmentCombinations
    }

    return null
  }

  /**
   * Analyze survey question responses for strategic insights.
   * Returns audience segments, question improvements, and recommendations.
   *
   * @param context - Survey context including question and response data
   * @returns Analysis result with insights, or null on error
   */
  async function questionAnalysis(
    context: string
  ): Promise<QuestionAnalysisResult | null> {
    const result = await execute({
      taskType: 'question_analysis',
      instruction: context,
      context: {}
    })

    if (result.success && result.data) {
      return result.data as QuestionAnalysisResult
    }

    return null
  }

  /**
   * Generate a complete flow from user instructions.
   * Creates questions, offers, personalization tokens, and flow structure.
   *
   * @param instructions - Description of what the flow should do
   * @returns Generated flow with all components, or null on error
   */
  async function bootstrapFromTemplate(
    instructions: string
  ): Promise<BootstrappedFlow | null> {
    const result = await execute({
      taskType: 'bootstrap_flow',
      instruction: instructions,
      context: {}
    })

    if (result.success && result.data) {
      return result.data as BootstrappedFlow
    }

    return null
  }

  /**
   * Generate ESP custom field sync values for segments (simple version).
   * This is the simpler API that just takes segment data without dimensionId.
   *
   * @param segmentData - Array of segment IDs and names
   * @returns Object with syncValues array, or null on error
   */
  async function generateSyncValuesSimple(
    segmentData: Array<{ id: string; name: string }>
  ): Promise<{ syncValues: Array<{ segmentId: string; syncValue: string }> } | null> {
    const result = await execute({
      taskType: 'generate_sync_values',
      instruction: 'Generate sync values for these segments',
      context: { segmentData }
    })

    if (result.success && result.data?.syncValues) {
      return { syncValues: result.data.syncValues }
    }

    return null
  }

  /**
   * Personalize content with Liquid templating for email segments.
   *
   * @param selectedText - The text content to personalize
   * @param surroundingContent - The surrounding context (for reference only)
   * @param segmentationDimensions - Array of segment data for personalization
   * @param instructions - Optional user instructions for personalization focus
   * @param liquidInstructions - Optional ESP-specific Liquid syntax instructions
   * @returns The personalized content with Liquid templating, or null on error
   */
  async function personalizeContent(
    selectedText: string,
    surroundingContent: string,
    segmentationDimensions: Array<{
      name: string
      questionText: string
      fieldName: string
      segments: Array<{
        segmentId: string
        name: string
        fieldValue: string
      }>
    }>,
    instructions?: string,
    liquidInstructions?: string
  ): Promise<string | null> {
    const result = await execute({
      taskType: 'personalize_content',
      instruction: instructions || 'Personalize the selected content for each segment',
      context: {
        selectedText,
        surroundingContent,
        segmentationDimensions,
        instructions: instructions || '',
        liquidInstructions: liquidInstructions || ''
      }
    })

    if (result.success) {
      // The AI returns the raw personalized content directly
      return result.content || result.rawContent || null
    }

    return null
  }

  return {
    // Convenience methods
    createQuestion,
    createOffer,
    generateCode,
    generateOfferVariants,
    generatePersonalization,
    generatePersonalizationTokens,
    generateSyncValues,
    generateSyncValuesSimple,
    personalizeContent,
    enrichChanges,
    enrichChangesWithDescriptions,
    generatePublishMessage,
    createQuestionInternalId,
    generateSegmentGroupCombinations,
    questionAnalysis,
    bootstrapFromTemplate,

    // State
    isGenerating,
    error,

    // Reset
    reset
  }
}
